import { isNumber, toNumber } from 'lodash-es';
import { DEFAULT_CURRENCY } from '../constants';
import { EventPriceDto, EventTicketsReservationDto, EventTicketsTotalsWithReservationsDto } from '../models/dto';
import { isBefore } from 'date-fns';
import { EventPriceTypesEnum } from '../enums';

export const numberFormatter = (
	value?: string | number,
	options: Intl.NumberFormatOptions & { delimiters?: DelimConfig } = {},
	locale = 'en-US'
): string => {
	const tempValue = value ? toNumber(value) : 0;

	const formatter = new Intl.NumberFormat(locale, {
		...(options ?? {}),
	});

	let formattedValue = formatter.format(tempValue);

	if (options.delimiters) {
		formattedValue = replacePriceDelimiter(options.delimiters, formattedValue);
	}

	return formattedValue;
};
export const priceFormatter = (
	price?: string | number,
	options: Omit<Intl.NumberFormatOptions, 'style'> & { delimiters?: DelimConfig } = {}
): string => {
	const value = (price ? toNumber(price) : 0) / 100;

	const formattedPrice = numberFormatter(value, {
		...(options ?? {}),
		style: 'currency',
		currency: options?.currency?.toUpperCase() || DEFAULT_CURRENCY.toUpperCase(),
		useGrouping: options?.useGrouping ?? true,
		minimumFractionDigits: options?.minimumFractionDigits ?? 0,
	});

	return formattedPrice;
};

type DelimConfig = { fractionDelim: string; thousandDelim: string };
export const thousandCommaDelim: DelimConfig = { fractionDelim: '.', thousandDelim: ',' };
export const thousandSpaceDelim: DelimConfig = { fractionDelim: '.', thousandDelim: ' ' };

// replace commas first then dots
export const replacePriceDelimiter = (config: DelimConfig, value: string): string =>
	value.replace(/,/g, config.thousandDelim).replace(/\./g, config.fractionDelim);

export const getEventPriceRange = (
	prices?: Pick<EventPriceDto, 'priceAmount'>[],
	showUnlisted?: boolean
): { min: EventPriceDto; max?: EventPriceDto } | undefined => {
	const validPrices: EventPriceDto[] = (prices as EventPriceDto[])?.filter(p =>
		showUnlisted ? true : p.tierTypeId !== EventPriceTypesEnum.Unlisted
	);

	if (!validPrices?.length) {
		return undefined;
	}

	if (validPrices?.length === 1) {
		return { min: validPrices[0] };
	}

	const defaultMaxPrice = { priceAmount: 0 };
	let minPrice: EventPriceDto = validPrices[0];
	let maxPrice = defaultMaxPrice;

	validPrices.forEach(eventPrice => {
		if (minPrice && eventPrice.priceAmount < minPrice.priceAmount) {
			minPrice = eventPrice;
		}

		if (maxPrice && eventPrice.priceAmount > maxPrice.priceAmount) {
			maxPrice = eventPrice;
		}
	});

	return {
		min: minPrice,
		max:
			maxPrice !== defaultMaxPrice && minPrice.priceAmount !== maxPrice.priceAmount
				? (maxPrice as EventPriceDto)
				: undefined,
	};
};

export const getEventPriceRangeString = (
	prices?: Pick<EventPriceDto, 'priceAmount'>[],
	showUnlisted?: boolean
): string => {
	const minMaxPrices = getEventPriceRange(prices, showUnlisted);

	if (minMaxPrices?.min) {
		return `${priceFormatter(minMaxPrices.min.priceAmount)}${
			minMaxPrices.max ? ` - ${priceFormatter(minMaxPrices.max.priceAmount)}` : ''
		}`;
	}

	return 'No prices';
};

export const isPriceSoldOut = (
	price: EventPriceDto,
	eventTicketsCountAndSpots?: EventTicketsTotalsWithReservationsDto,
	userTicketReservations?: EventTicketsReservationDto[]
): boolean => {
	if (!price) {
		return false;
	}

	const ticketsCount = price?.ticketsCount ?? 0;
	const minTicketsPerOrderNormalized = price?.minTicketsPerOrder ?? 1;

	const validTicketReservations = (userTicketReservations ?? []).filter(({ expiresAt }) =>
		isBefore(new Date(), expiresAt)
	);
	const reservedPriceTicketsCount = validTicketReservations.filter(
		r => r.baseEventPriceId === price.basePriceId
	).length;
	const priceSpots = eventTicketsCountAndSpots?.pricesSpots?.find(p => p.basePriceId === price.basePriceId);

	if (priceSpots) {
		const availableTickets = priceSpots.spotsCountWithReservations + reservedPriceTicketsCount;

		return availableTickets < minTicketsPerOrderNormalized;
	}

	if (!isNumber(price.maxTickets)) {
		return false;
	}

	const availableTickets = price.maxTickets > ticketsCount ? price.maxTickets - ticketsCount : 0;

	return ticketsCount >= price.maxTickets || availableTickets < minTicketsPerOrderNormalized;
};

export const canPreselectPriceTier = ({
	availablePublishedPrices,
	isPaidEvent,
	canCheckoutAfterEndDate,
	isEventSoldOut,
	isPurchaseAllowedDueToSalesStartTime,
	hasTicketReservationTimer,
}: {
	availablePublishedPrices?: EventPriceDto[];
	isPaidEvent: boolean;
	canCheckoutAfterEndDate: boolean;
	isEventSoldOut: boolean;
	isPurchaseAllowedDueToSalesStartTime: boolean;
	hasTicketReservationTimer: boolean;
}) =>
	isPaidEvent &&
	!isEventSoldOut &&
	isPurchaseAllowedDueToSalesStartTime &&
	canCheckoutAfterEndDate &&
	// do not preselect ticket if prices more than 1
	availablePublishedPrices?.length === 1 &&
	// do not preselect ticket if event has reservation time
	!hasTicketReservationTimer;

export const disableOpenPurchaseButton = ({
	isPaidEvent,
	isPastEvent,
	isEventSoldOut,
	ticketsQuantity,
	isPurchaseAllowedDueToSalesStartTime,
}: {
	isPaidEvent: boolean;
	isPastEvent: boolean;
	isEventSoldOut: boolean;
	ticketsQuantity?: number;
	isPurchaseAllowedDueToSalesStartTime: boolean;
}) => (isPaidEvent && !isPastEvent && !isEventSoldOut && !ticketsQuantity) || !isPurchaseAllowedDueToSalesStartTime;
